import React, { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ShareSavings = ({ amountLabel, className = "", iconSize = 16 }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const shareUrl = `${BACKEND}/api/share/savings?amount=${encodeURIComponent(amountLabel)}`;
  const text = `I've saved ${amountLabel} on LytePress finding local deals! Save More. Waste Less. 🌿 Join me:`;

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${shareUrl}`);
      setCopied(true);
      toast({ title: t("shs.copied"), description: t("shs.pasteItAnywhereTo") });
      setTimeout(() => setCopied(false), 1500);
    } catch { toast({ title: t("shs.couldnTCopy"), description: shareUrl }); }
    setOpen(false);
  };

  const onShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (navigator.share) {
      try { await navigator.share({ title: t("shs.myLytepressSavings"), text, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    setOpen((o) => !o);
  };

  const socials = [
    { name: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`, color: "#25D366" },
    { name: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, color: "#000000" },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, color: "#1877F2" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={onShare} data-testid="share-savings-btn" className={className} aria-label={t("shs.shareMySavings")}>
        <Share2 size={iconSize} /> {t("shs.shareMySavings")}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden p-1 text-left" data-testid="share-savings-menu">
          {socials.map((s) => (
            <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
              data-testid={`share-savings-${s.name.toLowerCase()}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-secondary transition-colors">
              <span className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: s.color }}>{s.name[0]}</span>
              Share on {s.name}
            </a>
          ))}
          <button type="button" onClick={copy} data-testid="share-savings-copy"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-secondary transition-colors">
            <span className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center">{copied ? <Check size={14} className="text-[#16a34a]" /> : <Copy size={14} />}</span>
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareSavings;
